/**
 * Admin Service
 * Handles all admin-related API calls for managing users, hairdressers, and salons
 */

import siteSetting from '../config/env';

// Use v2 API endpoint (new backend on port 5001)
const apiV2Url = siteSetting.api.v2;

/**
 * Get authorization headers
 * Note: v2 API uses 'accessToken' header, not 'Authorization: Bearer'
 */
function authHeader() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    accessToken: token || ''
  };
}

/**
 * Handle API response
 */
async function handleResponse(response) {
  const data = await response.json();
  if (!response.ok) {
    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }
  return data;
}

/**
 * Check admin status
 */
async function checkAdminStatus() {
  const response = await fetch(`${apiV2Url}admin/status`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get dashboard statistics
 */
async function getDashboardStats() {
  const response = await fetch(`${apiV2Url}admin/dashboard`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function linkMultiLocation(body) {
  const response = await fetch(`${apiV2Url}admin/multi-location/link`, {
    method: 'POST', headers: authHeader(), body: JSON.stringify(body)
  });
  return handleResponse(response);
}

/** Take one salon out of its location group (admin). Its owner, bookings, clients and money stay. */
async function unlinkMultiLocation(salonId) {
  const response = await fetch(`${apiV2Url}admin/multi-location/unlink`, {
    method: 'POST', headers: authHeader(), body: JSON.stringify({ salonId })
  });
  return handleResponse(response);
}

// ============================================
// USER MANAGEMENT
// ============================================

/**
 * Get all users with pagination and search
 * @param {Object} params - { page, limit, search, sortBy, sortOrder }
 */
async function getUsers(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await fetch(`${apiV2Url}admin/users?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get a single user by ID
 * @param {string} userId
 */
async function getUserById(userId) {
  const response = await fetch(`${apiV2Url}admin/users/${userId}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Create a new user
 * @param {Object} userData
 */
async function createUser(userData) {
  const response = await fetch(`${apiV2Url}admin/users`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify(userData)
  });
  return handleResponse(response);
}

/**
 * Update a user
 * @param {string} userId
 * @param {Object} userData
 */
async function updateUser(userId, userData) {
  const response = await fetch(`${apiV2Url}admin/users/${userId}`, {
    method: 'PUT',
    headers: authHeader(),
    body: JSON.stringify(userData)
  });
  return handleResponse(response);
}

/**
 * Delete a user
 * @param {string} userId
 */
async function deleteUser(userId) {
  const response = await fetch(`${apiV2Url}admin/users/${userId}`, {
    method: 'DELETE',
    headers: authHeader()
  });
  return handleResponse(response);
}

// ============================================
// HAIRDRESSER (PROFESSIONAL) MANAGEMENT
// ============================================

/**
 * Get all hairdressers with pagination and search
 * @param {Object} params - { page, limit, search, salonId, sortBy, sortOrder }
 */
async function getHairdressers(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.salonId) queryParams.append('salonId', params.salonId);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await fetch(`${apiV2Url}admin/hairdressers?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get a single hairdresser by ID
 * @param {string} hairdresserId
 */
async function getHairdresserById(hairdresserId) {
  const response = await fetch(`${apiV2Url}admin/hairdressers/${hairdresserId}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Create a new hairdresser
 * @param {Object} hairdresserData
 */
async function createHairdresser(hairdresserData) {
  const response = await fetch(`${apiV2Url}admin/hairdressers`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify(hairdresserData)
  });
  return handleResponse(response);
}

/**
 * Update a hairdresser
 * @param {string} hairdresserId
 * @param {Object} hairdresserData
 */
async function updateHairdresser(hairdresserId, hairdresserData) {
  const response = await fetch(`${apiV2Url}admin/hairdressers/${hairdresserId}`, {
    method: 'PUT',
    headers: authHeader(),
    body: JSON.stringify(hairdresserData)
  });
  return handleResponse(response);
}

/**
 * Delete a hairdresser
 * @param {string} hairdresserId
 */
async function deleteHairdresser(hairdresserId) {
  const response = await fetch(`${apiV2Url}admin/hairdressers/${hairdresserId}`, {
    method: 'DELETE',
    headers: authHeader()
  });
  return handleResponse(response);
}

// ============================================
// SALON (BUSINESS) MANAGEMENT
// ============================================

/**
 * Get all salons with pagination and search
 * @param {Object} params - { page, limit, search, sortBy, sortOrder, claimedFilter }
 */
async function getSalons(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.claimedFilter) queryParams.append('claimedFilter', params.claimedFilter);

  const response = await fetch(`${apiV2Url}admin/salons?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get a single salon by ID
 * @param {string} salonId
 */
async function getSalonById(salonId) {
  const response = await fetch(`${apiV2Url}admin/salons/${salonId}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Create a new salon
 * @param {Object} salonData
 */
async function createSalon(salonData) {
  const response = await fetch(`${apiV2Url}admin/salons`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify(salonData)
  });
  return handleResponse(response);
}

/**
 * Update a salon
 * @param {string} salonId
 * @param {Object} salonData
 */
async function updateSalon(salonId, salonData) {
  const response = await fetch(`${apiV2Url}admin/salons/${salonId}`, {
    method: 'PUT',
    headers: authHeader(),
    body: JSON.stringify(salonData)
  });
  return handleResponse(response);
}

/**
 * Delete a salon
 * @param {string} salonId
 */
async function deleteSalon(salonId) {
  const response = await fetch(`${apiV2Url}admin/salons/${salonId}`, {
    method: 'DELETE',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Upload a gallery image for a salon
 * @param {string} salonId
 * @param {File} file
 */
async function uploadSalonGalleryImage(salonId, file) {
  const formData = new FormData();
  formData.append('image', file);

  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${apiV2Url}admin/salons/${salonId}/gallery`, {
    method: 'POST',
    headers: {
      accessToken: token || ''
    },
    body: formData
  });
  return handleResponse(response);
}

/**
 * Delete a gallery image from a salon
 * @param {string} salonId
 * @param {string} imageUrl
 */
async function deleteSalonGalleryImage(salonId, imageUrl) {
  const response = await fetch(`${apiV2Url}admin/salons/${salonId}/gallery`, {
    method: 'DELETE',
    headers: authHeader(),
    body: JSON.stringify({ imageUrl })
  });
  return handleResponse(response);
}

/**
 * Reorder gallery images for a salon
 * @param {string} salonId
 * @param {Array} reorderData - Array of { url, order } objects
 */
async function reorderSalonGalleryImages(salonId, reorderData) {
  const response = await fetch(`${apiV2Url}admin/salons/${salonId}/gallery/reorder`, {
    method: 'PUT',
    headers: authHeader(),
    body: JSON.stringify({ images: reorderData })
  });
  return handleResponse(response);
}

// ============================================
// BOOKING MANAGEMENT
// ============================================

/**
 * Get all bookings with pagination and search
 * @param {Object} params - { page, limit, search, status, salonId, hairdresserId, sortBy, sortOrder }
 */
async function getBookings(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.status !== undefined && params.status !== '') queryParams.append('status', params.status);
  if (params.salonId && params.salonId !== '') queryParams.append('salonId', params.salonId);
  if (params.hairdresserId && params.hairdresserId !== '')
    queryParams.append('hairdresserId', params.hairdresserId);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await fetch(`${apiV2Url}admin/bookings?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Reconciliation audit feed — every action the money sweeps took (product
 * payout retries, missed-webhook finalisations, gift-card issues), with the
 * failure reason on anything that didn't recover.
 * @param {Object} params - { skip, limit, outcome: 'recovered'|'failed' }
 */
async function getPaymentBible(params = {}) {
  const queryParams = new URLSearchParams();
  ['page', 'limit', 'eventType', 'channel', 'salonId', 'bookingId', 'from', 'to', 'search'].forEach((k) => {
    if (params[k]) queryParams.append(k, params[k]);
  });
  if (params.problemsOnly) queryParams.append('problemsOnly', 'true');
  const response = await fetch(`${apiV2Url}admin/payment-bible?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getPaymentBiblePayments(params = {}) {
  const queryParams = new URLSearchParams();
  ['page', 'limit', 'channel', 'salonId', 'bookingId', 'from', 'to', 'search'].forEach((k) => {
    if (params[k]) queryParams.append(k, params[k]);
  });
  if (params.problemsOnly) queryParams.append('problemsOnly', 'true');
  const response = await fetch(`${apiV2Url}admin/payment-bible/payments?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getPaymentBibleHeadlines(params = {}) {
  const queryParams = new URLSearchParams();
  ['from', 'to', 'salonId'].forEach((k) => { if (params[k]) queryParams.append(k, params[k]); });
  const response = await fetch(`${apiV2Url}admin/payment-bible/headlines?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

// ---- Policies (versioned Partner Terms / Client Terms / Privacy) ----------

async function getPolicyVersions(type) {
  const response = await fetch(`${apiV2Url}policy/${type}/versions`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function getPolicyVersion(type, version) {
  const response = await fetch(`${apiV2Url}policy/${type}/versions/${version}`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function getPolicyCurrent(type) {
  const response = await fetch(`${apiV2Url}policy/${type}`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function publishPolicy(type, payload) {
  const response = await fetch(`${apiV2Url}policy/${type}`, {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return handleResponse(response);
}

async function reconcilePaymentBibleTransfer(eventId) {
  const response = await fetch(`${apiV2Url}admin/payment-bible/reconcile/${eventId}`, {
    method: 'POST',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getPaymentBibleBooking(bookingId) {
  const response = await fetch(`${apiV2Url}admin/payment-bible/booking/${bookingId}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getReconciliations(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.skip) queryParams.append('skip', params.skip);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.outcome) queryParams.append('outcome', params.outcome);
  const response = await fetch(`${apiV2Url}admin/reconciliations?${queryParams.toString()}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get a single booking by ID
 * @param {string} bookingId
 */
async function getBookingById(bookingId) {
  const response = await fetch(`${apiV2Url}admin/bookings/${bookingId}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Update a booking (status, notes, etc.)
 * @param {string} bookingId
 * @param {Object} bookingData
 */
async function updateBooking(bookingId, bookingData) {
  const response = await fetch(`${apiV2Url}admin/bookings/${bookingId}`, {
    method: 'PUT',
    headers: authHeader(),
    body: JSON.stringify(bookingData)
  });
  return handleResponse(response);
}

// ============================================
// BUSINESS IMPORT
// ============================================

/**
 * Run a dry-run of the business import (preview stats, no writes)
 * @param {Array} data - Array of business objects from uploaded JSON
 */
async function importBusinessesDryRun(data) {
  const response = await fetch(`${apiV2Url}admin/import/businesses/dry-run`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify({ data })
  });
  return handleResponse(response);
}

/**
 * Import enriched businesses as unclaimed salon profiles
 * @param {Array} data - Array of business objects from uploaded JSON
 * @param {number} limit - Number of businesses to import (0 = all)
 */
async function importBusinesses(data, limit = 0) {
  const response = await fetch(`${apiV2Url}admin/import/businesses`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify({ data, limit })
  });
  return handleResponse(response);
}

// ============================================
// ENRICHMENT
// ============================================

/**
 * Enrich salons with Google Places data
 * @param {Array<string>} salonIds - Specific salon IDs to enrich (empty for batch)
 * @param {number} limit - Number of salons to enrich if no specific IDs
 */
async function enrichWithGooglePlaces(salonIds = [], limit = 10) {
  const response = await fetch(`${apiV2Url}admin/enrich/google-places`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify({ salonIds, limit })
  });
  return handleResponse(response);
}

// ============================================
// CLIENT IMPORT
// ============================================

/**
 * Upload client list from Excel file
 * @param {string} hairdresserId - The hairdresser ID to associate clients with
 * @param {File} file - Excel file (.xlsx) with columns: Name, Email, Phone, Note
 */
async function uploadClientList(hairdresserId, file) {
  const formData = new FormData();
  formData.append('clientXls', file);
  formData.append('hairdresserId', hairdresserId);

  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${apiV2Url}user/uploadClient`, {
    method: 'POST',
    headers: {
      accessToken: token || ''
    },
    body: formData
  });
  return handleResponse(response);
}

/**
 * Update existing clients from Excel file
 * @param {string} hairdresserId - The hairdresser ID to associate clients with
 * @param {File} file - Excel file (.xlsx) with columns: Name, Email, Phone, Note
 */
async function updateClientList(hairdresserId, file) {
  const formData = new FormData();
  formData.append('clientXls', file);
  formData.append('hairdresserId', hairdresserId);

  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${apiV2Url}user/updateUploadClient`, {
    method: 'POST',
    headers: {
      accessToken: token || ''
    },
    body: formData
  });
  return handleResponse(response);
}

// ============================================
// EMPLOYMENT TYPE SWITCH
// ============================================

/**
 * Get booking stats for a hairdresser (total count, date range)
 * @param {string} hairdresserId
 */
async function getHairdresserBookingStats(hairdresserId) {
  const response = await fetch(`${apiV2Url}admin/hairdressers/${hairdresserId}/booking-stats`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Switch employment type for a hairdresser with optional booking commission updates
 * @param {string} hairdresserId
 * @param {Object} switchData - { newEmploymentTypeId, salonCommissionRate, productCommissionRate, voucherCommissionRate, updateBookings, fromDate }
 */
async function switchEmploymentType(hairdresserId, switchData) {
  const response = await fetch(`${apiV2Url}admin/hairdressers/${hairdresserId}/switch-employment`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify(switchData)
  });
  return handleResponse(response);
}

// ============================================
// MERCHANT APPROVALS (signup hardening)
// ============================================

/**
 * List salon owners awaiting Beu approval to add a payout bank / be paid out
 * (merchantStatus pending/flagged, or who requested a bank change).
 * @param {Object} [params] - optional { status: 'pending' | 'flagged' }
 */
async function getMerchantApprovals(params = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  const qs = q.toString();
  const response = await fetch(`${apiV2Url}admin/merchant-approvals${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Approve a salon owner — unblocks their (and their self-employed staff's)
 * add-bank flow + payouts.
 */
async function approveMerchant(hairdresserId) {
  const response = await fetch(`${apiV2Url}admin/merchant-approvals/${hairdresserId}/approve`, {
    method: 'POST',
    headers: authHeader()
  });
  return handleResponse(response);
}

// ============================================
// FINANCIALS
// ============================================

/**
 * Get financials overview — KPI counts + MTD/prev-month revenue summary + processing snapshot
 */
function withDateRangeQuery(params) {
  const q = new URLSearchParams();
  if (params?.startDate instanceof Date) q.set('startDate', params.startDate.toISOString());
  if (params?.endDate instanceof Date) q.set('endDate', params.endDate.toISOString());
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function getFinancialsOverview(params) {
  const response = await fetch(`${apiV2Url}admin/financials/overview${withDateRangeQuery(params)}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get payment-processing drill-down (Bucket #2) — by channel, tier ladder
 */
async function getFinancialsProcessing(params) {
  const response = await fetch(`${apiV2Url}admin/financials/processing${withDateRangeQuery(params)}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getFinancialsOffPlatform(params) {
  const response = await fetch(`${apiV2Url}admin/financials/off-platform${withDateRangeQuery(params)}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getFinancialsSubscriptions(params) {
  const response = await fetch(`${apiV2Url}admin/financials/subscriptions${withDateRangeQuery(params)}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

async function getFinancialsKpis(params) {
  const response = await fetch(`${apiV2Url}admin/financials/kpis${withDateRangeQuery(params)}`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

/**
 * Get salons status overview — every salon classified live / off-platform /
 * inactive / onboarding / stalled / dormant for ops triage.
 */
async function getFinancialsSalons() {
  const response = await fetch(`${apiV2Url}admin/financials/salons`, {
    method: 'GET',
    headers: authHeader()
  });
  return handleResponse(response);
}

// Export all functions

/* ───────────────────────── Sales deals ───────────────────────── */

/** Role-aware admin-shell entry: 'admin' | 'sales' (403 for everyone else). */
async function checkDealsAccess() {
  const response = await fetch(`${apiV2Url}admin/deals/access`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function getDeals(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, v); });
  const response = await fetch(`${apiV2Url}admin/deals?${qs.toString()}`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function getDealsSummary() {
  const response = await fetch(`${apiV2Url}admin/deals/summary`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function getDealManagers() {
  const response = await fetch(`${apiV2Url}admin/deals/managers`, { method: 'GET', headers: authHeader() });
  return handleResponse(response);
}

async function updateDeal(dealId, body) {
  const response = await fetch(`${apiV2Url}admin/deals/${dealId}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(body) });
  return handleResponse(response);
}

async function addDealNote(dealId, text) {
  const response = await fetch(`${apiV2Url}admin/deals/${dealId}/notes`, { method: 'POST', headers: authHeader(), body: JSON.stringify({ text }) });
  return handleResponse(response);
}

async function materialiseDeal(dealId) {
  const response = await fetch(`${apiV2Url}admin/deals/${dealId}/materialise`, { method: 'POST', headers: authHeader(), body: JSON.stringify({}) });
  return handleResponse(response);
}

async function createDeal(body) {
  const response = await fetch(`${apiV2Url}admin/deals`, { method: 'POST', headers: authHeader(), body: JSON.stringify(body) });
  return handleResponse(response);
}

export const adminService = {
  // Status & Dashboard
  checkAdminStatus,
  getDashboardStats,
  linkMultiLocation,
  unlinkMultiLocation,

  // Sales deals
  checkDealsAccess,
  getDeals,
  getDealsSummary,
  getDealManagers,
  updateDeal,
  addDealNote,
  materialiseDeal,
  createDeal,

  // Sales deals
  checkDealsAccess,
  getDeals,
  getDealsSummary,
  getDealManagers,
  updateDeal,
  addDealNote,
  materialiseDeal,
  createDeal,

  // Financials
  getFinancialsOverview,
  getFinancialsProcessing,
  getFinancialsOffPlatform,
  getFinancialsSubscriptions,
  getFinancialsKpis,
  getFinancialsSalons,

  // Users
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,

  // Hairdressers (Professionals)
  getHairdressers,
  getHairdresserById,
  createHairdresser,
  updateHairdresser,
  deleteHairdresser,

  // Salons (Businesses)
  getSalons,
  getSalonById,
  createSalon,
  updateSalon,
  deleteSalon,
  uploadSalonGalleryImage,
  deleteSalonGalleryImage,
  reorderSalonGalleryImages,

  // Bookings
  getBookings,
  getReconciliations,
  getPaymentBible,
  getPaymentBiblePayments,
  getPaymentBibleHeadlines,
  getPaymentBibleBooking,
  reconcilePaymentBibleTransfer,
  getPolicyVersions,
  getPolicyVersion,
  getPolicyCurrent,
  publishPolicy,
  getBookingById,
  updateBooking,

  // Business Import
  importBusinessesDryRun,
  importBusinesses,

  // Enrichment
  enrichWithGooglePlaces,

  // Client Import
  uploadClientList,
  updateClientList,

  // Employment Type Switch
  getHairdresserBookingStats,
  switchEmploymentType,

  // Merchant approvals
  getMerchantApprovals,
  approveMerchant
};

export default adminService;
